import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import { Typography } from '@mui/material';
import { DataGrid, GridFooterContainer, GridPagination, useGridApiRef } from '@mui/x-data-grid';
import useApiManager from '../../../utils/Api/apiManager';
import { useNavigate, Link } from "react-router-dom";
import { ProposalStatus, DealStatus } from '../../../utils/enums'
import { format } from 'date-fns';

function CustomFooter({ totalAmount }) {
    return (
        <GridFooterContainer>
            <Box flexGrow={1} pl={2}>
                <Typography variant="subtitle2">Total Amount: ${totalAmount}</Typography>
            </Box>
            <GridPagination />
        </GridFooterContainer>
    );
}

const ProposalsDataGrid = ({ filters, selectedRows, onSelectionModelChange, unassigned, triggerFetch, setTriggerFetch, apiRef }) => {
    const [rows, setRows] = useState([]);
    const [rowCount, setRowCount] = useState(0);
    const [totalAmount, setTotalAmount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [sortModel, setSortModel] = useState([]);
    const navigate = useNavigate();
    const { post } = useApiManager();
    const [paginationModel, setPaginationModel] = React.useState({
        page: 0,
        pageSize: 50,
    });
    const [filterModel, setFilterModel] = useState({ items: [] });
    const [filterParams, setFilterParams] = useState([]);
    const localApiRef = useGridApiRef();

    useEffect(() => {
        apiRef.current = localApiRef.current;
    }, [apiRef, localApiRef]);


    const columns = [
        { field: 'id', headerName: 'ID', flex: 0.5 },
        {
            field: 'topic',
            headerName: 'Topic',
            flex: 1,
            renderCell: (params) => {
                const displayName = params.value?.trim() || "(N/A)"; // fallback if null, undefined, or empty
                return (
                    <Link
                        to={`/proposals/${params.row.id}`}
                        onClick={(e) => e.stopPropagation()}
                        style={{ color: 'inherit' }}
                    >
                        {displayName}
                    </Link>
                );
            },
        },
        { field: 'amount', headerName: 'Amount', flex: 1 },
        { field: 'proposalStatus', headerName: 'Proposal Status', flex: 1 },
        { field: 'dealStatus', headerName: 'Deal Status', flex: 1 },
        { field: 'deadline', headerName: 'Deadline', flex: 1 },
        { field: 'date', headerName: 'Date', flex: 1 },
        { field: 'duration', headerName: 'Duration', flex: 1 },
        { field: 'createdBy', headerName: 'Created By', flex: 1 },
        { field: 'assignTo', headerName: 'Assigned to', flex: 1 },
        { field: 'location', headerName: 'Location', flex: 1 },
    ];

    const transformData = (data) => {
        return data.map(item => ({
            id: item.id,
            topic: item.topic,
            amount: item?.amount != null ? `$ ${item.amount}` : '',
            proposalStatus: ProposalStatus?.[item.proposalStatus]?.label || '',
            dealStatus: DealStatus?.[item.dealStatus]?.label || '',
            deadline: item.deadline ? format(new Date(item.deadline), 'MMMM do yyyy') : '',
            date: item.date ? format(new Date(item.date), 'MMMM do yyyy') : '',
            duration: item.duration || '',
            createdBy: item.createdBy || '',
            assignTo: Array.isArray(item.assignTo)
                ? item.assignTo.join(', ')
                : item.assignTo || '',
            location: item.location || '',
        }));
    };

    useEffect(() => {
        setFilterParams(filters);
    }, [filters]);

    useEffect(() => {
        const fetchAndSetData = async () => {
            setLoading(true);
            try {


                const sortField = sortModel.length > 0 ? sortModel[0].field : 'createdAt';
                const sortDescending = sortModel.length > 0 ? sortModel[0].sort === 'desc' : true;

                let url = "/proposals/paged";

                const data = await post(url, {
                    pageNumber: paginationModel.page + 1,
                    pageSize: paginationModel.pageSize,
                    filters: filterParams,
                    sortField: sortField,
                    sortDescending: sortDescending
                });
                setRows(transformData(data.items));
                setRowCount(data.totalCount);
                setTotalAmount(data.totalAmount || 0);
            } catch (error) {
                console.error('Error fetching data:', error);
            } finally {
                setLoading(false);
                if (setTriggerFetch) {
                    setTriggerFetch(false);
                }
            }
        };

        fetchAndSetData();
    }, [paginationModel, filterParams, sortModel, triggerFetch]);

    const handleRowClick = (params) => {
        navigate(`/proposals/${params.id}`);
    };

    return (
        <DataGrid
            apiRef={localApiRef}
            rows={rows}
            columns={columns}
            rowCount={rowCount}
            paginationMode="server"
            sortingMode="server"
            columnVisibilityModel={{
                id: false,
            }}
            getRowId={(row) => row.id}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[50, 75, 100]}
            onSortModelChange={(newSortModel) => setSortModel(newSortModel)}
            filterMode="server"
            onFilterModelChange={(newFilterModel) => {
                console.log(newFilterModel);
                setFilterModel(newFilterModel);
            }}
            checkboxSelection
            rowSelectionModel={selectedRows}
            onRowSelectionModelChange={onSelectionModelChange}
            disableRowSelectionOnClick
            loading={loading}
            onRowClick={handleRowClick}
            sx={{
                height: '100%', // Fills the parent container
                '& .MuiDataGrid-virtualScroller': {
                    overflow: 'auto', // Ensures scrollbars appear when necessary
                },
            }}
            slots={{
                footer: () => <CustomFooter totalAmount={totalAmount} />,
            }}
        />
    );
};

export default ProposalsDataGrid;

